import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take, map } from 'rxjs/operators';
import { UserService, CurrentUserInfoResponse } from '../services/user.service';

/**
 * AdminGuard — allows users with DB role = 'Admin' OR 'Mat Coordinator'.
 * No MSAL token role check — DB user_role_cd is the single source of truth.
 */
@Injectable({ providedIn: 'root' })
export class AdminGuard implements CanActivate {
  constructor(
    private userService: UserService,
    private router: Router
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    return this.userService.currentUserInfo$.pipe(
      take(1),
      switchMap((info: CurrentUserInfoResponse | null) => {
        if (info) {
          return of(this.evaluateAccess(info));
        }
        return this.userService.getUserInfo().pipe(
          map((fresh: CurrentUserInfoResponse) => {
            this.userService.setCurrentUserInfo(fresh);
            return this.evaluateAccess(fresh);
          }),
          catchError(() => of(this.router.parseUrl('/unauthorized')))
        );
      })
    );
  }

  private evaluateAccess(info: CurrentUserInfoResponse): boolean | UrlTree {
    const user = info?.user;
    if (!info?.success || !user) {
      return this.router.parseUrl('/unauthorized');
    }
    if (user.is_active === false) {
      return this.router.parseUrl('/unauthorized');
    }
    const role = (user.user_role_cd || '').trim().toLowerCase();
    if (role === 'mat coordinator') {
      return true;
    }
    return this.router.parseUrl('/unauthorized');
  }
}
